import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { z } from "zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { receiveRmaLine } from "@/lib/inventory.functions";
import {
  listShippedOrders, listRmas, listRmaLines, listRmaSkus, listRmaLocations,
  createRma as createRmaFnImport, addRmaLine,
} from "@/lib/returns.functions";
import { recordDisposition, listDispositionsForRma } from "@/lib/returns-disposition.functions";
import {
  shopReturnRates, purchaseReturnLabel, voidReturnLabel,
  attachCustomerTracking, emailReturnLabel, listReturnLabels,
} from "@/lib/return-labels.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { ExportMenu } from "@/components/ui/export-menu";
import { ScopeBar } from "@/components/scope-bar";
import { HelpButton } from "@/components/kb/help-button";
import { ContextualTip } from "@/components/kb/contextual-tip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { Plus, Loader2, PackageCheck, FilePlus2, Truck, Download, Mail, Ban, Link2, Hammer, FileText, Settings2, Inbox, Gavel, Sparkles } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/returns")({
  validateSearch: z.object({ rmaId: z.string().uuid().optional() }),
  component: ReturnsPage,
});

function ReturnsPage() {
  const qc = useQueryClient();
  const { tenantId, facilityId, customerId, customers } = useScope();
  const receiveRmaFn = useServerFn(receiveRmaLine);
  const listShippedFn = useServerFn(listShippedOrders);
  const listRmasFn = useServerFn(listRmas);
  const listLinesFn = useServerFn(listRmaLines);
  const listSkusFn = useServerFn(listRmaSkus);
  const listLocsFn = useServerFn(listRmaLocations);
  const createRmaFn = useServerFn(createRmaFnImport);
  const addRmaLineFn = useServerFn(addRmaLine);
  const recordDispFn = useServerFn(recordDisposition);
  const listDispFn = useServerFn(listDispositionsForRma);
  const shopRatesFn = useServerFn(shopReturnRates);
  const buyLabelFn = useServerFn(purchaseReturnLabel);
  const voidLabelFn = useServerFn(voidReturnLabel);
  const attachTrackingFn = useServerFn(attachCustomerTracking);
  const emailLabelFn = useServerFn(emailReturnLabel);
  const listLabelsFn = useServerFn(listReturnLabels);
  const [activeRma, setActiveRma] = useState<string | null>(null);
  const search = Route.useSearch();
  useEffect(() => {
    if (search.rmaId && search.rmaId !== activeRma) setActiveRma(search.rmaId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.rmaId]);

  // Create RMA dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [cCustomer, setCCustomer] = useState("");
  const [cNumber, setCNumber] = useState("");
  const [cOrder, setCOrder] = useState("");
  const [cReason, setCReason] = useState("");
  const [cNotes, setCNotes] = useState("");
  const [creating, setCreating] = useState(false);

  const { data: openOrders } = useQuery({
    queryKey: ["rma-orders", tenantId, facilityId, cCustomer],
    enabled: createOpen && !!tenantId && !!facilityId && !!cCustomer,
    queryFn: () => listShippedFn({ data: { tenantId: tenantId!, facilityId: facilityId!, customerId: cCustomer } }),
  });

  const createRma = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId || !facilityId) return;
    if (!cCustomer || !cNumber.trim()) return toast.error("Customer and RMA # are required");
    setCreating(true);
    try {
      const res = await runServerFn<{ id: string }>(
        () => createRmaFn({ data: {
          tenantId, facilityId, customerId: cCustomer,
          rmaNumber: cNumber.trim(), orderId: cOrder || null,
          reason: cReason.trim() || null, notes: cNotes.trim() || null,
        } }) as Promise<{ id: string }>,
        "Create RMA",
      );
      toast.success("RMA created");
      setCreateOpen(false);
      setCNumber(""); setCOrder(""); setCReason(""); setCNotes("");
      qc.invalidateQueries({ queryKey: ["rmas"] });
      setActiveRma(res.id);
    } catch { /* toast handled */ } finally { setCreating(false); }
  };

  const { data: rmas, isLoading } = useQuery({
    queryKey: ["rmas", tenantId, facilityId, customerId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listRmasFn({ data: { tenantId: tenantId!, facilityId: facilityId!, customerId: customerId ?? null } }),
  });
  const cName = (id: string) => customers.find((c) => c.id === id)?.name ?? "—";

  const rma = useMemo(() => rmas?.find((r) => r.id === activeRma) ?? null, [rmas, activeRma]);
  const { data: lines, refetch: refetchLines } = useQuery({
    queryKey: ["rma-lines", activeRma], enabled: !!activeRma,
    queryFn: () => listLinesFn({ data: { rmaId: activeRma! } }),
  });
  const { data: skus } = useQuery({
    queryKey: ["rma-skus", rma?.customer_id], enabled: !!rma?.customer_id,
    queryFn: () => listSkusFn({ data: { customerId: rma!.customer_id } }),
  });
  const { data: locations } = useQuery({
    queryKey: ["rma-locations", facilityId], enabled: !!facilityId,
    queryFn: () => listLocsFn({ data: { facilityId: facilityId! } }),
  });
  const skuLabel = (id: string) => { const s = skus?.find((x) => x.id === id); return s ? `${s.sku_code} — ${s.name}` : "—"; };

  const [addOpen, setAddOpen] = useState(false);
  const [skuId, setSkuId] = useState(""); const [qty, setQty] = useState(""); const [adding, setAdding] = useState(false);
  const addLine = async (e: React.FormEvent) => {
    e.preventDefault();
    const n = parseInt(qty, 10);
    if (!activeRma || !skuId || !n || n <= 0) return toast.error("Pick a SKU and quantity");
    setAdding(true);
    try {
      await runServerFn(
        () => addRmaLineFn({ data: { rmaId: activeRma, skuId, qtyExpected: n } }),
        "Add line",
      );
      toast.success("Line added"); setAddOpen(false); setSkuId(""); setQty(""); refetchLines();
    } catch { /* toast handled */ } finally { setAdding(false); }
  };

  const [recvFor, setRecvFor] = useState<string | null>(null);
  const [rQty, setRQty] = useState(""); const [rLoc, setRLoc] = useState(""); const [rCond, setRCond] = useState<"new"|"used"|"damaged"|"destroy">("new");
  const [receiving, setReceiving] = useState(false);
  const receive = async (e: React.FormEvent) => {
    e.preventDefault();
    const n = parseInt(rQty, 10);
    if (!recvFor || !n || n <= 0 || !rLoc) return toast.error("Pick location and qty");
    setReceiving(true);
    try {
      await runServerFn(
        () => receiveRmaFn({ data: { rmaLineId: recvFor, qty: n, locationId: rLoc, condition: rCond } }),
        "Receive return",
      );
      toast.success("Received"); setRecvFor(null); setRQty(""); setRLoc(""); setRCond("new");
      qc.invalidateQueries({ queryKey: ["rma-lines", activeRma] });
      qc.invalidateQueries({ queryKey: ["rmas"] });
      qc.invalidateQueries({ queryKey: ["balances"] });
    } catch { /* toast handled */ } finally { setReceiving(false); }
  };

  // Disposition dialog state
  const [dispFor, setDispFor] = useState<string | null>(null);
  const [dQty, setDQty] = useState("");
  const [dCond, setDCond] = useState<"new"|"used"|"damaged"|"destroy">("used");
  const [dAction, setDAction] = useState<"restock"|"scrap"|"return_to_vendor"|"quarantine">("restock");
  const [dNotes, setDNotes] = useState("");
  const [dispSaving, setDispSaving] = useState(false);
  const { data: dispData, refetch: refetchDisp } = useQuery({
    queryKey: ["rma-dispositions", activeRma],
    enabled: !!activeRma,
    queryFn: () => listDispFn({ data: { rmaId: activeRma! } }),
  });
  const recordDisp = async (e: React.FormEvent) => {
    e.preventDefault();
    const n = parseInt(dQty, 10);
    if (!tenantId || !dispFor || !n || n <= 0) return toast.error("Pick a quantity");
    setDispSaving(true);
    try {
      await runServerFn(
        () => recordDispFn({ data: { tenantId, rmaLineId: dispFor, qty: n, condition: dCond, action: dAction, notes: dNotes.trim() || undefined } }),
        "Record disposition",
      );
      toast.success("Disposition recorded");
      setDispFor(null); setDQty(""); setDNotes(""); setDCond("used"); setDAction("restock");
      refetchDisp();
    } catch { /* toast handled */ } finally { setDispSaving(false); }
  };

  return (
    <>
      <PageHeader
        title="Returns"
        description="Process incoming returns from your brands."
        actions={
          <div className="flex items-center gap-2">
            <ScopeBar />
            <HelpButton moduleKey="returns" />
            {tenantId ? (
              <ExportMenu
                entity="rmas"
                tenantId={tenantId}
                facilityId={facilityId}
                customerId={customerId}
                getRows={() => (rmas ?? []).map((r: any) => ({
                  rma_number: r.rma_number ?? r.number ?? "",
                  customer: cName(r.customer_id),
                  order_number: r.order_number ?? r.orders?.order_number ?? "",
                  status: r.status,
                  reason: r.reason ?? "",
                  created_at: r.created_at,
                }))}
                columns={["rma_number","customer","order_number","status","reason","created_at"]}
                disabled={!rmas || rmas.length === 0}
              />
            ) : null}
            <Button size="sm" onClick={() => { setCCustomer(customerId ?? ""); setCreateOpen(true); }}>
              <FilePlus2 className="h-4 w-4 mr-2" />New RMA
            </Button>
          </div>
        }
      />
      <div className="p-4 sm:p-6 space-y-4">
        <ContextualTip tipKey="returns_intro" />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" asChild>
            <Link to="/app/returns-refurb"><Hammer className="h-4 w-4 mr-1" />Refurb queue</Link>
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to="/app/returns-receiving"><Inbox className="h-4 w-4 mr-1" />Receiving</Link>
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to="/app/returns-disposition"><Gavel className="h-4 w-4 mr-1" />Disposition</Link>
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to="/app/returns-credits"><FileText className="h-4 w-4 mr-1" />Credit memos</Link>
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to="/app/returns-rules"><Settings2 className="h-4 w-4 mr-1" />Disposition rules</Link>
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to="/app/quality-intelligence"><Sparkles className="h-4 w-4 mr-1" />Quality intelligence</Link>
          </Button>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
        <div className="border rounded-lg bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>RMA #</TableHead><TableHead>Customer</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {isLoading ? (<TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>)
              : rmas && rmas.length > 0 ? rmas.map((r) => (
                <TableRow key={r.id} className={activeRma === r.id ? "bg-accent" : ""}>
                  <TableCell className="font-medium">{r.rma_number}</TableCell>
                  <TableCell className="text-muted-foreground">{cName(r.customer_id)}</TableCell>
                  <TableCell><StatusPill tone={statusTone(r.status)} dot>{r.status}</StatusPill></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => setActiveRma(r.id)}>Open →</Button>
                  </TableCell>
                </TableRow>
              )) : (<TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No RMAs.</TableCell></TableRow>)}
            </TableBody>
          </Table>
        </div>
        <div className="border rounded-lg bg-card">
          {!rma ? (
            <div className="p-8 text-center text-muted-foreground text-sm">Select an RMA to process its lines.</div>
          ) : (
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">{rma.rma_number}</div>
                  <div className="text-sm text-muted-foreground">{rma.reason ?? "—"}</div>
                </div>
                <Dialog open={addOpen} onOpenChange={setAddOpen}>
                  <DialogTrigger asChild><Button size="sm" variant="outline"><Plus className="h-4 w-4 mr-2" />Add line</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Add RMA line</DialogTitle></DialogHeader>
                    <form onSubmit={addLine} className="space-y-4">
                      <div className="space-y-2"><Label>SKU</Label>
                        <Select value={skuId} onValueChange={setSkuId}>
                          <SelectTrigger><SelectValue placeholder="Select SKU" /></SelectTrigger>
                          <SelectContent>{skus?.map((s) => <SelectItem key={s.id} value={s.id}>{s.sku_code} — {s.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2"><Label>Quantity</Label><Input type="number" min={1} required value={qty} onChange={(e) => setQty(e.target.value)} /></div>
                      <DialogFooter><Button type="submit" disabled={adding}>{adding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add</Button></DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="overflow-x-auto"><Table>
                <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead className="text-right">Expected</TableHead><TableHead className="text-right">Received</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {lines && lines.length > 0 ? lines.map((l) => {
                    const remaining = l.qty_expected - l.qty_received;
                    return (
                      <TableRow key={l.id}>
                        <TableCell>{skuLabel(l.sku_id)}</TableCell>
                        <TableCell className="text-right tabular-nums">{l.qty_expected}</TableCell>
                        <TableCell className="text-right tabular-nums">{l.qty_received}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" disabled={remaining <= 0} onClick={() => { setRecvFor(l.id); setRQty(String(remaining)); }}>
                            <PackageCheck className="h-4 w-4 mr-2" />Receive
                          </Button>
                          <Button size="sm" variant="ghost" className="ml-2" disabled={l.qty_received <= 0} onClick={() => { setDispFor(l.id); setDQty(String(l.qty_received)); }}>
                            Dispose
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  }) : (<TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">No lines yet.</TableCell></TableRow>)}
                </TableBody>
              </Table></div>
              {dispData?.dispositions && dispData.dispositions.length > 0 && (
                <div className="border-t pt-3">
                  <div className="text-sm font-medium mb-2">Dispositions</div>
                  <div className="overflow-x-auto"><Table>
                    <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>Condition</TableHead><TableHead>Action</TableHead><TableHead>Notes</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {dispData.dispositions.map((d: any) => (
                        <TableRow key={d.id}>
                          <TableCell className="text-xs">{skuLabel(d.sku_id)}</TableCell>
                          <TableCell className="text-right tabular-nums">{d.qty}</TableCell>
                          <TableCell><Badge variant="outline">{d.condition}</Badge></TableCell>
                          <TableCell><Badge>{d.action}</Badge></TableCell>
                          <TableCell className="text-xs text-muted-foreground">{d.notes ?? "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table></div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {rma && (
        <ReturnLabelsPanel
          rmaId={rma.id}
          rmaNumber={rma.rma_number}
          shopRatesFn={shopRatesFn}
          buyLabelFn={buyLabelFn}
          voidLabelFn={voidLabelFn}
          attachTrackingFn={attachTrackingFn}
          emailLabelFn={emailLabelFn}
          listLabelsFn={listLabelsFn}
        />
      )}
      <Dialog open={!!recvFor} onOpenChange={(o) => { if (!o) setRecvFor(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Receive return</DialogTitle></DialogHeader>
          <form onSubmit={receive} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Quantity</Label><Input type="number" min={1} required value={rQty} onChange={(e) => setRQty(e.target.value)} /></div>
              <div className="space-y-2"><Label>Condition</Label>
                <Select value={rCond} onValueChange={(v) => setRCond(v as typeof rCond)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New (restock)</SelectItem>
                    <SelectItem value="used">Used (restock)</SelectItem>
                    <SelectItem value="damaged">Damaged</SelectItem>
                    <SelectItem value="destroy">Destroy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Location</Label>
              <Select value={rLoc} onValueChange={setRLoc}>
                <SelectTrigger><SelectValue placeholder="Pick a bin" /></SelectTrigger>
                <SelectContent>{locations?.map((l) => <SelectItem key={l.id} value={l.id}>{l.code}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <DialogFooter><Button type="submit" disabled={receiving}>{receiving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Confirm</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!dispFor} onOpenChange={(o) => { if (!o) setDispFor(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record disposition</DialogTitle></DialogHeader>
          <form onSubmit={recordDisp} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Quantity</Label><Input type="number" min={1} required value={dQty} onChange={(e) => setDQty(e.target.value)} /></div>
              <div className="space-y-2"><Label>Condition</Label>
                <Select value={dCond} onValueChange={(v) => setDCond(v as typeof dCond)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="used">Used</SelectItem>
                    <SelectItem value="damaged">Damaged</SelectItem>
                    <SelectItem value="destroy">Destroy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Action</Label>
              <Select value={dAction} onValueChange={(v) => setDAction(v as typeof dAction)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="restock">Restock to sellable</SelectItem>
                  <SelectItem value="quarantine">Quarantine for QA</SelectItem>
                  <SelectItem value="return_to_vendor">Return to vendor</SelectItem>
                  <SelectItem value="scrap">Scrap / destroy</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Notes</Label>
              <Input value={dNotes} onChange={(e) => setDNotes(e.target.value)} placeholder="Optional" />
            </div>
            <DialogFooter><Button type="submit" disabled={dispSaving}>{dispSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save disposition</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create RMA</DialogTitle></DialogHeader>
          <form onSubmit={createRma} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Customer</Label>
                <Select value={cCustomer} onValueChange={(v) => { setCCustomer(v); setCOrder(""); }}>
                  <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>RMA #</Label>
                <Input required value={cNumber} onChange={(e) => setCNumber(e.target.value)} placeholder="RMA-1001" />
              </div>
            </div>
            <div className="space-y-2"><Label>Original order (optional)</Label>
              <Select value={cOrder} onValueChange={setCOrder}>
                <SelectTrigger><SelectValue placeholder={cCustomer ? "Pick an order" : "Select a customer first"} /></SelectTrigger>
                <SelectContent>{openOrders?.map((o) => <SelectItem key={o.id} value={o.id}>{o.order_number}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Reason</Label>
              <Input value={cReason} onChange={(e) => setCReason(e.target.value)} placeholder="Wrong size, damaged, etc." />
            </div>
            <div className="space-y-2"><Label>Notes</Label>
              <Input value={cNotes} onChange={(e) => setCNotes(e.target.value)} />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={creating}>{creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      </div>
    </>
  );
}

/* -------------------- Return Labels Panel -------------------- */
type ServerFn<T, R> = (args: { data: T }) => Promise<R>;

function fmtMoney(c: number | null | undefined) {
  if (c == null) return "—";
  return `$${(c / 100).toFixed(2)}`;
}

function ReturnLabelsPanel(props: {
  rmaId: string;
  rmaNumber: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  shopRatesFn: ServerFn<any, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buyLabelFn: ServerFn<any, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  voidLabelFn: ServerFn<any, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attachTrackingFn: ServerFn<any, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emailLabelFn: ServerFn<any, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listLabelsFn: ServerFn<any, any>;
}) {
  const qc = useQueryClient();
  const [weight, setWeight] = useState("16");
  const [length, setLength] = useState("12");
  const [width, setWidth] = useState("9");
  const [height, setHeight] = useState("3");
  const [carriers, setCarriers] = useState<Record<string, boolean>>({ ups: true, usps: true });
  const [rates, setRates] = useState<Array<{ carrier: string; service_code: string; service_name: string; rate_cents: number; est_transit_days: number | null }>>([]);
  const [shopping, setShopping] = useState(false);
  const [buying, setBuying] = useState<string | null>(null);

  const [attachOpen, setAttachOpen] = useState(false);
  const [aTracking, setATracking] = useState("");
  const [aCarrier, setACarrier] = useState<"ups"|"usps"|"fedex"|"other">("ups");

  const [emailOpen, setEmailOpen] = useState<{ id: string; tracking: string } | null>(null);
  const [eEmail, setEEmail] = useState("");
  const [eMessage, setEMessage] = useState("");

  const { data: labels, refetch: refetchLabels } = useQuery({
    queryKey: ["return-labels", props.rmaId],
    queryFn: () => props.listLabelsFn({ data: { rmaId: props.rmaId } }) as Promise<any[]>,
  });

  const pkg = () => ({
    weight_oz: Math.max(0.1, parseFloat(weight) || 0),
    length_in: Math.max(1, parseFloat(length) || 0),
    width_in: Math.max(1, parseFloat(width) || 0),
    height_in: Math.max(1, parseFloat(height) || 0),
  });

  const shop = async () => {
    const selected = Object.entries(carriers).filter(([, v]) => v).map(([k]) => k);
    if (!selected.length) return toast.error("Pick at least one carrier");
    setShopping(true);
    try {
      const res = await runServerFn<{ rates: typeof rates }>(
        () => props.shopRatesFn({ data: { rmaId: props.rmaId, carriers: selected, pkg: pkg() } }),
        "Shop return rates",
      );
      setRates(res.rates ?? []);
      if (!res.rates?.length) toast.info("No rates returned. Check addresses and credentials.");
    } catch { /* toasted */ } finally { setShopping(false); }
  };

  const buy = async (r: typeof rates[number]) => {
    const key = `${r.carrier}-${r.service_code}`;
    setBuying(key);
    try {
      await runServerFn(
        () => props.buyLabelFn({ data: {
          rmaId: props.rmaId, carrier: r.carrier, serviceCode: r.service_code,
          serviceName: r.service_name, pkg: pkg(),
          idempotencyKey: `rl-${props.rmaId}-${Date.now()}`,
        } }),
        "Purchase return label",
      );
      toast.success("Return label purchased");
      setRates([]);
      refetchLabels();
      qc.invalidateQueries({ queryKey: ["rmas"] });
    } catch { /* toasted */ } finally { setBuying(null); }
  };

  const voidIt = async (id: string) => {
    const reason = window.prompt("Reason for voiding this label?") ?? "";
    try {
      await runServerFn(
        () => props.voidLabelFn({ data: { returnLabelId: id, reason: reason || null, idempotencyKey: `vl-${id}-${Date.now()}` } }),
        "Void label",
      );
      toast.success("Label voided");
      refetchLabels();
    } catch { /* toasted */ }
  };

  const attach = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!aTracking.trim()) return toast.error("Tracking number required");
    try {
      await runServerFn(
        () => props.attachTrackingFn({ data: { rmaId: props.rmaId, carrier: aCarrier, trackingNumber: aTracking.trim() } }),
        "Attach tracking",
      );
      toast.success("Customer tracking attached");
      setAttachOpen(false); setATracking("");
      refetchLabels();
    } catch { /* toasted */ }
  };

  const sendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailOpen || !eEmail) return;
    try {
      await runServerFn(
        () => props.emailLabelFn({ data: { returnLabelId: emailOpen.id, toEmail: eEmail, message: eMessage || undefined } }),
        "Email label",
      );
      toast.success("Label emailed");
      setEmailOpen(null); setEEmail(""); setEMessage("");
    } catch { /* toasted */ }
  };

  return (
    <div className="mt-4 border rounded-lg bg-card">
      <div className="p-4 border-b flex items-center justify-between">
        <div>
          <div className="font-semibold flex items-center gap-2"><Truck className="h-4 w-4" />Return labels</div>
          <div className="text-xs text-muted-foreground">{props.rmaNumber}</div>
        </div>
        <Dialog open={attachOpen} onOpenChange={setAttachOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline"><Link2 className="h-4 w-4 mr-2" />Customer-provided tracking</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Attach customer-provided tracking</DialogTitle></DialogHeader>
            <form onSubmit={attach} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Carrier</Label>
                  <Select value={aCarrier} onValueChange={(v) => setACarrier(v as typeof aCarrier)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ups">UPS</SelectItem>
                      <SelectItem value="usps">USPS</SelectItem>
                      <SelectItem value="fedex">FedEx</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Tracking #</Label>
                  <Input value={aTracking} onChange={(e) => setATracking(e.target.value)} required />
                </div>
              </div>
              <DialogFooter><Button type="submit">Attach</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="p-4 space-y-4">
        {/* Package + rate shop */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="space-y-1"><Label className="text-xs">Weight (oz)</Label><Input value={weight} onChange={(e) => setWeight(e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Length (in)</Label><Input value={length} onChange={(e) => setLength(e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Width (in)</Label><Input value={width} onChange={(e) => setWidth(e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Height (in)</Label><Input value={height} onChange={(e) => setHeight(e.target.value)} /></div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {(["ups","usps","fedex"] as const).map((c) => (
            <label key={c} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!carriers[c]} onChange={(e) => setCarriers({ ...carriers, [c]: e.target.checked })} />
              {c.toUpperCase()}
            </label>
          ))}
          <Button size="sm" onClick={shop} disabled={shopping}>
            {shopping ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Truck className="h-4 w-4 mr-2" />}
            Shop rates
          </Button>
        </div>

        {rates.length > 0 && (
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Carrier</TableHead><TableHead>Service</TableHead>
                <TableHead className="text-right">Transit</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rates.map((r) => {
                  const key = `${r.carrier}-${r.service_code}`;
                  return (
                    <TableRow key={key}>
                      <TableCell className="uppercase text-xs">{r.carrier}</TableCell>
                      <TableCell>{r.service_name}</TableCell>
                      <TableCell className="text-right">{r.est_transit_days ? `${r.est_transit_days}d` : "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtMoney(r.rate_cents)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" onClick={() => buy(r)} disabled={buying === key}>
                          {buying === key && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Buy
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Existing labels */}
        <div className="border rounded-md overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Carrier</TableHead><TableHead>Tracking</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Cost</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {labels && labels.length > 0 ? labels.map((l: any) => (
                <TableRow key={l.id}>
                  <TableCell className="uppercase text-xs">{l.carrier}</TableCell>
                  <TableCell className="font-mono text-xs">{l.tracking_number}</TableCell>
                  <TableCell>
                    <Badge variant={l.status === "voided" ? "destructive" : l.status === "customer_provided" ? "outline" : "secondary"}>
                      {l.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{l.provided_by_customer ? "—" : fmtMoney(l.rate_cents)}</TableCell>
                  <TableCell className="text-right space-x-1">
                    {l.label_url && (
                      <Button size="sm" variant="ghost" asChild>
                        <a href={l.label_url} target="_blank" rel="noreferrer"><Download className="h-4 w-4" /></a>
                      </Button>
                    )}
                    {l.label_url && l.status === "active" && (
                      <Button size="sm" variant="ghost" onClick={() => { setEmailOpen({ id: l.id, tracking: l.tracking_number }); }}>
                        <Mail className="h-4 w-4" />
                      </Button>
                    )}
                    {l.status === "active" && !l.provided_by_customer && (
                      <Button size="sm" variant="ghost" onClick={() => voidIt(l.id)}>
                        <Ban className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground text-sm">No labels yet. Shop rates above to purchase one.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!emailOpen} onOpenChange={(o) => { if (!o) setEmailOpen(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Email return label</DialogTitle></DialogHeader>
          <form onSubmit={sendEmail} className="space-y-3">
            <div className="space-y-2"><Label>To</Label>
              <Input type="email" required value={eEmail} onChange={(e) => setEEmail(e.target.value)} placeholder="customer@example.com" />
            </div>
            <div className="space-y-2"><Label>Message (optional)</Label>
              <Input value={eMessage} onChange={(e) => setEMessage(e.target.value)} maxLength={500} />
            </div>
            <DialogFooter><Button type="submit"><Mail className="h-4 w-4 mr-2" />Send</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
